import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CalendarCheck, KeyRound, TrendingUp, Sparkles, Wrench, Calculator,
  Percent, Briefcase, FileSpreadsheet, Download, Loader2,
} from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const REPORTS = [
  { key: "bookings", label: "Report Prenotazioni", desc: "Elenco prenotazioni ed economics per soggiorno", icon: CalendarCheck, to: "/bookings", color: "#2563eb" },
  { key: "owners", label: "Report Proprietari", desc: "Canoni, commissioni e liquidazioni", icon: KeyRound, to: "/owners", color: "#7c3aed" },
  { key: "revenue", label: "Report Revenue", desc: "Ricavi per periodo, appartamento e canale", icon: TrendingUp, to: "/accounting", color: "#059669" },
  { key: "cleanings", label: "Report Pulizie", desc: "Costi e attività di pulizia", icon: Sparkles, to: "/cleanings", color: "#0891b2" },
  { key: "maintenance", label: "Report Manutenzioni", desc: "Interventi e costi di manutenzione", icon: Wrench, to: "/maintenance", color: "#d97706" },
  { key: "economic", label: "Report Economico", desc: "Conto economico sintetico: ricavi − costi = margine", icon: Calculator, to: "/accounting", color: "#4f46e5" },
  { key: "vat", label: "Report IVA", desc: "Imponibile e IVA per periodo", icon: Percent, to: null, color: "#db2777" },
  { key: "accountant", label: "Report Commercialista", desc: "Pacchetto completo pronto per lo studio", icon: Briefcase, to: null, color: "#334155" },
];

export default function ReportCenter() {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [fmt, setFmt] = useState("xlsx");
  const [busy, setBusy] = useState(false);
  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  const doExport = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/finance/export?format=${fmt}&year=${year}&month=${month}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "csv" ? `finance_os_${year}_${String(month).padStart(2, "0")}_csv.zip` : `finance_os_${year}_${String(month).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export Finance OS generato");
    } catch (e) {
      toast.error("Errore durante l'export");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="report-center">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Report Center</h1>
        <p className="mt-1 text-sm text-slate-500">Un unico punto per esportare i dati di Urban OS verso l'amministrazione e il commercialista, senza doppi inserimenti.</p>
      </div>

      {/* Esporta Finance OS — formato ufficiale */}
      <Card className="overflow-hidden border-0 bg-gradient-to-br from-slate-900 to-slate-800 p-6 text-white shadow-lg" data-testid="finance-export-card">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-500/20 text-emerald-300"><FileSpreadsheet size={24} /></div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">Esporta Finance OS</h2>
                <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[11px] font-medium text-emerald-300">formato ufficiale · finance_export.v1</span>
              </div>
              <p className="mt-1 max-w-xl text-sm text-slate-300">Genera il file standard (Excel multi-foglio o CSV) con prenotazioni, economics, pagamenti, liquidazioni, costi e KPI. Ogni record ha ID e timestamp per la sincronizzazione senza duplicati.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1 block text-xs text-slate-400">Mese</label>
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger data-testid="export-month" className="h-9 w-36 border-white/20 bg-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-400">Anno</label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger data-testid="export-year" className="h-9 w-28 border-white/20 bg-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-400">Formato</label>
              <Select value={fmt} onValueChange={setFmt}>
                <SelectTrigger data-testid="export-format" className="h-9 w-32 border-white/20 bg-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="xlsx">Excel (.xlsx)</SelectItem><SelectItem value="csv">CSV (.zip)</SelectItem></SelectContent>
              </Select>
            </div>
            <Button data-testid="export-finance-btn" onClick={doExport} disabled={busy} className="h-9 bg-emerald-500 text-white hover:bg-emerald-600">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
              Esporta
            </Button>
          </div>
        </div>
      </Card>

      {/* Report cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          const soon = !r.to;
          return (
            <Card key={r.key} data-testid={`report-card-${r.key}`}
              onClick={() => r.to && navigate(r.to)}
              className={`group flex items-start gap-4 p-5 transition-all ${soon ? "opacity-60" : "cursor-pointer hover:-translate-y-0.5 hover:shadow-md"}`}>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: `${r.color}15`, color: r.color }}><Icon size={22} /></div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-900">{r.label}</h3>
                  {soon && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">in arrivo</span>}
                </div>
                <p className="mt-0.5 text-sm text-slate-500">{r.desc}</p>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
